/**
 * Generates preview artefacts in the browser with WebCodecs (via mediabunny): thumbnail, filmstrip
 * sprite, waveform peaks and a low-resolution, short-GOP proxy video. Replaces server-side ffmpeg.
 */
import {
  AudioSampleSink,
  BufferTarget,
  CanvasSink,
  Conversion,
  Mp4OutputFormat,
  Output,
  getFirstEncodableVideoCodec,
  type InputVideoTrack,
} from 'mediabunny';
import type { MediaAsset } from '@ve/core';
import { decodeImage, transcodeToSdr, transferOf, type MediaInput } from '@ve/engine';
import type { DerivativeContext, DerivativeGenerator, DerivativeKind, DerivativeResult } from '../types.ts';
import { canvasToJpeg, createCanvas, openInput, throwIfAborted, toBlob } from './input.ts';
import {
  PROXY_KEYFRAME_INTERVAL,
  THUMBNAIL_WIDTH,
  filmstripTimestamps,
  fitWidth,
  needsProxy,
  planFilmstrip,
  proxyBitrate,
  proxySize,
  thumbnailTime,
} from './plans.ts';
import { throttleProgress } from './progress.ts';
import { WaveformBuilder } from './waveform-builder.ts';

/** Background for transparent stills in JPEG thumbnails (matches the editor's dark UI). */
const THUMB_BACKGROUND = '#1b1d21';

export class BrowserDerivativeGenerator implements DerivativeGenerator {
  plan(asset: MediaAsset): DerivativeKind[] {
    switch (asset.kind) {
      case 'video': {
        const kinds: DerivativeKind[] = ['thumbnail', 'filmstrip'];
        if (asset.hasAudio) kinds.push('waveform');
        if (needsProxy(asset.height)) kinds.push('proxy');
        return kinds;
      }
      case 'audio':
        return ['waveform'];
      case 'image':
        return ['thumbnail'];
    }
  }

  async generate(
    kind: DerivativeKind,
    asset: MediaAsset,
    input: MediaInput,
    ctx: DerivativeContext,
  ): Promise<DerivativeResult> {
    throwIfAborted(ctx.signal);
    // Decoders produce thousands of chunks; only forward meaningful progress steps.
    ctx = { signal: ctx.signal, onProgress: throttleProgress(ctx.onProgress) };
    try {
      switch (kind) {
        case 'thumbnail':
          return asset.kind === 'image'
            ? await imageThumbnail(input, ctx)
            : await videoThumbnail(asset, input, ctx);
        case 'filmstrip':
          return await filmstrip(asset, input, ctx);
        case 'waveform':
          return await waveform(asset, input, ctx);
        case 'proxy':
          return await proxy(asset, input, ctx);
      }
    } catch (err) {
      // Cancellation surfaces as library-specific errors; normalise to a standard AbortError.
      throwIfAborted(ctx.signal);
      throw err;
    }
  }
}

// ---------------------------------------------------------------------------------------------

async function primaryVideo(input: ReturnType<typeof openInput>): Promise<InputVideoTrack> {
  const track = await input.getPrimaryVideoTrack();
  if (!track) throw new Error('The file has no video track');
  return track;
}

async function imageThumbnail(source: MediaInput, ctx: DerivativeContext): Promise<DerivativeResult> {
  const bitmap = await decodeImage(await toBlob(source, ctx.signal));
  try {
    throwIfAborted(ctx.signal);
    const size = fitWidth(bitmap.width, bitmap.height, THUMBNAIL_WIDTH);
    const canvas = createCanvas(size.width, size.height);
    const g = canvas.getContext('2d')!;
    g.fillStyle = THUMB_BACKGROUND;
    g.fillRect(0, 0, size.width, size.height);
    g.imageSmoothingQuality = 'high';
    g.drawImage(bitmap, 0, 0, size.width, size.height);
    ctx.onProgress(1);
    return { blob: await canvasToJpeg(canvas) };
  } finally {
    bitmap.close();
  }
}

async function videoThumbnail(
  asset: MediaAsset,
  source: MediaInput,
  ctx: DerivativeContext,
): Promise<DerivativeResult> {
  const input = openInput(source);
  try {
    const track = await primaryVideo(input);
    const size = fitWidth(await track.getDisplayWidth(), await track.getDisplayHeight(), THUMBNAIL_WIDTH);
    const sink = new CanvasSink(track, { width: size.width, height: size.height, fit: 'fill', poolSize: 1 });
    const frame = (await sink.getCanvas(thumbnailTime(asset.duration))) ?? (await sink.getCanvas(0));
    if (!frame) throw new Error('Could not decode a frame for the thumbnail');
    throwIfAborted(ctx.signal);
    const canvas = createCanvas(size.width, size.height);
    canvas.getContext('2d')!.drawImage(frame.canvas, 0, 0, size.width, size.height);
    ctx.onProgress(1);
    return { blob: await canvasToJpeg(canvas) };
  } finally {
    input.dispose();
  }
}

async function filmstrip(
  asset: MediaAsset,
  source: MediaInput,
  ctx: DerivativeContext,
): Promise<DerivativeResult> {
  const input = openInput(source);
  try {
    const track = await primaryVideo(input);
    const width = asset.width ?? (await track.getDisplayWidth());
    const height = asset.height ?? (await track.getDisplayHeight());
    const duration = asset.duration ?? (await track.computeDuration());
    const info = planFilmstrip(duration, width, height);
    const sink = new CanvasSink(track, {
      width: info.tileWidth,
      height: info.tileHeight,
      fit: 'cover',
      poolSize: 2,
    });
    const sprite = createCanvas(info.count * info.tileWidth, info.tileHeight);
    const g = sprite.getContext('2d')!;
    g.fillStyle = '#000';
    g.fillRect(0, 0, sprite.width, sprite.height);

    let i = 0;
    let previous: OffscreenCanvas | null = null;
    for await (const frame of sink.canvasesAtTimestamps(filmstripTimestamps(info, duration))) {
      throwIfAborted(ctx.signal);
      const x = i * info.tileWidth;
      if (frame) g.drawImage(frame.canvas, x, 0, info.tileWidth, info.tileHeight);
      else if (previous) g.drawImage(previous, x, 0); // gap in the stream: repeat the last tile
      if (frame) {
        previous ??= createCanvas(info.tileWidth, info.tileHeight);
        previous.getContext('2d')!.drawImage(frame.canvas, 0, 0, info.tileWidth, info.tileHeight);
      }
      i++;
      ctx.onProgress(i / info.count);
    }
    return { blob: await canvasToJpeg(sprite, 0.75), filmstrip: info };
  } finally {
    input.dispose();
  }
}

async function waveform(
  asset: MediaAsset,
  source: MediaInput,
  ctx: DerivativeContext,
): Promise<DerivativeResult> {
  const input = openInput(source);
  try {
    const track = await input.getPrimaryAudioTrack();
    if (!track) throw new Error('The file has no audio track');
    const duration = asset.duration ?? (await track.computeDuration());
    const builder = new WaveformBuilder();
    // Samples are streamed and released one by one; the decoded audio is never held in full.
    for await (const sample of new AudioSampleSink(track).samples()) {
      try {
        throwIfAborted(ctx.signal);
        const planes: Float32Array[] = [];
        for (let c = 0; c < sample.numberOfChannels; c++) {
          const plane = new Float32Array(sample.numberOfFrames);
          sample.copyTo(plane, { planeIndex: c, format: 'f32-planar' });
          planes.push(plane);
        }
        builder.add(sample.timestamp, sample.sampleRate, planes);
        if (duration > 0) ctx.onProgress(Math.min(1, (sample.timestamp + sample.duration) / duration));
      } finally {
        sample.close();
      }
    }
    const result = builder.finish(duration > 0 ? duration : undefined);
    ctx.onProgress(1);
    return { blob: new Blob([JSON.stringify(result)], { type: 'application/json' }) };
  } finally {
    input.dispose();
  }
}

async function proxy(
  asset: MediaAsset,
  source: MediaInput,
  ctx: DerivativeContext,
): Promise<DerivativeResult> {
  const input = openInput(source);
  try {
    const track = await primaryVideo(input);
    const size = proxySize(
      asset.width ?? (await track.getDisplayWidth()),
      asset.height ?? (await track.getDisplayHeight()),
    );
    const codec = await getFirstEncodableVideoCodec(['avc', 'hevc', 'vp9', 'av1'], size);
    if (!codec) throw new Error('This browser cannot encode video, so no proxy can be made');
    const bitrate = proxyBitrate(size.width, size.height, asset.fps);

    if (transferOf(await track.getColorSpace()) !== 'sdr') {
      // HDR: an SDR proxy tone-mapped by the compositor, so the preview matches the export.
      const blob = await transcodeToSdr(source, {
        ...size,
        codec,
        bitrate,
        keyFrameInterval: PROXY_KEYFRAME_INTERVAL,
        signal: ctx.signal,
        onProgress: ctx.onProgress,
      });
      throwIfAborted(ctx.signal);
      ctx.onProgress(1);
      return { blob };
    }

    const target = new BufferTarget();
    const output = new Output({ format: new Mp4OutputFormat({ fastStart: 'in-memory' }), target });
    const conversion = await Conversion.init({
      input,
      output,
      tracks: 'primary',
      video: {
        width: size.width,
        height: size.height,
        fit: 'fill',
        codec,
        bitrate,
        keyFrameInterval: PROXY_KEYFRAME_INTERVAL,
        forceTranscode: true,
      },
      audio: { discard: true },
      showWarnings: false,
    });
    if (!conversion.isValid) {
      const reasons = conversion.discardedTracks
        .filter((t) => t.reason !== 'discarded_by_user')
        .map((t) => t.reason)
        .join(', ');
      throw new Error(`Proxy conversion not possible (${reasons || 'unknown reason'})`);
    }
    conversion.onProgress = (p) => ctx.onProgress(Math.min(1, Math.max(0, p)));
    const onAbort = () => void conversion.cancel();
    ctx.signal.addEventListener('abort', onAbort, { once: true });
    try {
      await conversion.execute();
    } finally {
      ctx.signal.removeEventListener('abort', onAbort);
    }
    throwIfAborted(ctx.signal);
    if (!target.buffer) throw new Error('Proxy encoding produced no output');
    ctx.onProgress(1);
    return { blob: new Blob([target.buffer], { type: 'video/mp4' }) };
  } finally {
    input.dispose();
  }
}
